<template>
  <div class="navbar-wrapper" :class="{ 'travel-page': showFullNav }">
    <!-- Floating Save-my-spot pill for the waitlist landing page -->
    <transition name="float-pill">
      <button
        v-if="isWaitlistPage && isPastVideo"
        @click="scrollToJoin"
        class="floating-save-pill"
        aria-label="Save my spot on the waitlist"
      >
        <span class="floating-save-pill-text">Save my spot</span>
        <span class="floating-save-pill-arrow" aria-hidden="true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </span>
      </button>
    </transition>

    <!-- Full navbar bar (travel page + non-waitlist routes only) -->
    <div
      v-if="!isWaitlistPage"
      class="
        w-full
        px-6
        py-4
        lg:px-10 lg:py-6
        flex flex-row
        justify-between
        items-center
        transition-all
        duration-100
        ease-[cubic-bezier(0.4,0,0.2,1)]
        rounded-b-md
      "
      :class="[
        showFullNav
          ? scrollProgress > 0
            ? 'fixed top-0 left-0 right-0 z-50 navbar-glass'
            : 'fixed top-0 left-0 right-0 z-50 navbar-transparent'
          : isPastVideo && !showFullNav
          ? 'fixed top-0 left-0 right-0 z-50 navbar-glass'
          : 'bg-transparent',
      ]"
      :style="{
        paddingTop:
          scrollProgress > 0
            ? `${Math.max(1, 1.5 - scrollProgress * 0.8)}rem`
            : '1rem',
        paddingBottom:
          scrollProgress > 0
            ? `${Math.max(1, 1.5 - scrollProgress * 0.8)}rem`
            : '1rem',
      }"
    >
      <div class="flex items-center">
        <router-link
          class="
            -m-1.5
            p-1.5
            transition-transform
            hover:scale-105
            flex flex-col
            items-center
            gap-0.5
          "
          to="/"
        >
          <img
            class="h-8 md:h-10 w-auto transition-all duration-700 ease-in-out"
            src="../assets/logo/logo.svg"
            alt="Logo"
          />
        </router-link>
      </div>

      <!-- Desktop Navigation -->
      <div
        v-if="showFullNav"
        class="
          hidden
          lg:flex
          flex-row
          gap-8
          xl:gap-14
          items-center
          justify-center
          text-gray-600
          transition-all
        "
      >
        <router-link
          class="hover:text-ternBlue hover:font-bold whitespace-nowrap"
          to="/"
        >
          Waitlist
        </router-link>
        <a
          class="hover:text-ternBlue hover:font-bold whitespace-nowrap"
          href="/visa"
        >
          Visa Checker
        </a>
        <a
          class="hover:text-ternBlue hover:font-bold whitespace-nowrap"
          href="https://travelwithtern.substack.com/"
          target="_blank"
        >
          Blog
        </a>
        <a
          class="
            desktop-login-button
            text-white
            hover:text-ternBlue
            font-bold
            leading-6
            border border-ternBlue
            bg-ternBlue
            hover:bg-white
            text-center
            rounded-full
            px-6
            py-1
            transition-all
            duration-300
            whitespace-nowrap
          "
          href="https://app.heytilly.ai/auth"
        >
          <span>Log in</span>
        </a>
      </div>

      <!-- Mobile Navigation -->
      <div class="lg:hidden flex flex-row gap-3 sm:gap-4 items-center relative">
        <!-- Log in button for travel page mobile -->
        <a
          v-if="showFullNav"
          class="
            mobile-login-button
            text-white
            font-bold
            leading-6
            border
            text-center
            rounded-full
            px-4
            py-1.5
            transition-all
            duration-300
            border-ternBlue
            bg-ternBlue
            hover:bg-white hover:text-ternBlue
            text-sm
            whitespace-nowrap
          "
          href="https://app.heytilly.ai/auth"
        >
          <span>Log in</span>
        </a>

        <!-- Mobile menu button for travel page -->
        <button
          v-if="showFullNav"
          @click="toggleMenu"
          class="
            menu-button
            relative
            z-50
            w-9
            h-9
            sm:w-10 sm:h-10
            flex
            items-center
            justify-center
          "
          :class="isWaitlistPage ? 'text-gray-800' : 'text-ternBlue'"
        >
          <span class="sr-only">Open menu</span>
          <div
            class="
              w-6
              flex flex-col
              items-end
              justify-center
              gap-1.5
              transition-all
              duration-300
            "
          >
            <span
              class="block h-0.5 rounded-full transition-all duration-300"
              :class="[
                isMenuOpen ? 'w-6 rotate-45 translate-y-2' : 'w-6',
                isWaitlistPage ? 'bg-white' : 'bg-ternBlue',
              ]"
            ></span>
            <span
              class="block h-0.5 rounded-full transition-all duration-300"
              :class="[
                isMenuOpen ? 'opacity-0' : 'w-4 opacity-100',
                isWaitlistPage ? 'bg-white' : 'bg-ternBlue',
              ]"
            ></span>
            <span
              class="block h-0.5 rounded-full transition-all duration-300"
              :class="[
                isMenuOpen ? 'w-6 -rotate-45 -translate-y-2' : 'w-5',
                isWaitlistPage ? 'bg-white' : 'bg-ternBlue',
              ]"
            ></span>
          </div>
        </button>

        <!-- Mobile Menu Dropdown for Travel Page -->
        <div
          v-if="isMenuOpen && showFullNav"
          class="
            mobile-menu-dropdown
            absolute
            top-full
            right-0
            mt-3
            z-40
            transform
            origin-top-right
            transition-all
            duration-300
          "
          :class="[isMenuOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0']"
        >
          <nav
            class="
              flex flex-col
              items-start
              space-y-2
              sm:space-y-3
              transition-all
            "
          >
            <router-link @click="closeMenu" class="mobile-menu-item" to="/">
              Waitlist
            </router-link>
            <a @click="closeMenu" class="mobile-menu-item" href="/visa">
              Visa Checker
            </a>
            <a
              @click="closeMenu"
              class="mobile-menu-item"
              href="https://travelwithtern.substack.com/"
              target="_blank"
            >
              Blog
            </a>
          </nav>
        </div>
      </div>
    </div>

    <!-- Spacer to account for fixed navbar -->
    <div
      v-if="showFullNav"
      class="transition-all duration-700 ease-in-out"
      :style="{
        height:
          scrollProgress > 0
            ? `${Math.max(56, 72 - scrollProgress * 16)}px`
            : '88px',
      }"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'Navbar',
  data() {
    return {
      isMenuOpen: false,
      scrolled: false,
      scrollProgress: 0,
      lastScrollY: 0,
      ticking: false,
    };
  },
  computed: {
    showFullNav() {
      // Show full navigation only on /travel page
      return this.$route.path === '/travel';
    },
    isWaitlistPage() {
      return this.$route.path === '/';
    },
    isPastVideo() {
      // Check if we've scrolled past the hero section (80% of viewport height)
      return this.isWaitlistPage && this.lastScrollY > window.innerHeight * 0.8;
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    // Initial check for scroll position
    this.handleScroll();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    handleScroll() {
      this.lastScrollY = window.scrollY;

      // Use requestAnimationFrame for smooth performance
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          // Calculate scroll progress (0 to 1) over the first 100px for non-waitlist pages
          this.scrollProgress = Math.min(1, this.lastScrollY / 100);
          this.scrolled = this.lastScrollY > 60;
          this.ticking = false;
        });
        this.ticking = true;
      }
    },
    scrollToJoin() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      // Emit event using global event bus if available
      if (this.emitter) {
        this.emitter.emit('scroll-to-join');
      }
    },
  },
};
</script>

<style scoped>
/* Smooth transitions for mobile menu */
.menu-button span {
  transform-origin: center;
}

/* Active link styling */
.router-link-active {
  font-weight: 500;
}

/* Glassmorphism for waitlist page navbar */
.navbar-transparent {
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-bottom: none;
}

.navbar-glass {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: #1a1a1a;
}

/* White text for waitlist page when transparent */
.navbar-transparent a,
.navbar-transparent .router-link {
  color: #1a1410 !important;
}

.navbar-transparent a:hover,
.navbar-transparent .router-link:hover {
  color: #58a8e1 !important;
}

/* Dark text for travel page when transparent (light background) */
.navbar-wrapper.travel-page .navbar-transparent a,
.navbar-wrapper.travel-page .navbar-transparent .router-link {
  color: #1a1a1a !important;
}

.navbar-wrapper.travel-page .navbar-transparent a:hover,
.navbar-wrapper.travel-page .navbar-transparent .router-link:hover {
  color: #58a8e1 !important;
}

/* Black text for frosted glass navbar */
.navbar-glass a,
.navbar-glass .router-link {
  color: #1a1a1a !important;
}

.navbar-glass a:hover,
.navbar-glass .router-link:hover {
  color: rgba(0, 0, 0, 0.7) !important;
}

/* Ensure child divs also have the right text color */
.navbar-glass div {
  color: #1a1a1a;
}

/* Floating Save-my-spot pill (waitlist page) */
.floating-save-pill {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem 0.65rem 1.4rem;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  color: #ffffff;
  background: linear-gradient(135deg, #58a8e1 0%, #4ba8c9 100%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 8px 24px rgba(88, 168, 225, 0.32),
    0 2px 6px rgba(15, 42, 65, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.25);
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s ease, background 0.3s ease;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.floating-save-pill:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 12px 32px rgba(88, 168, 225, 0.42),
    0 4px 10px rgba(15, 42, 65, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.floating-save-pill:hover .floating-save-pill-arrow {
  transform: translateX(3px);
}

.floating-save-pill:active {
  transform: translateY(-1px) scale(1);
}

.floating-save-pill-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@media (min-width: 768px) {
  .floating-save-pill {
    top: 1.5rem;
    right: 1.75rem;
    padding: 0.75rem 1.4rem 0.75rem 1.6rem;
    font-size: 1rem;
  }
}

/* Float-pill enter/leave animations */
.float-pill-enter-active {
  transition: opacity 0.45s ease,
    transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.float-pill-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.float-pill-enter-from {
  opacity: 0;
  transform: translateY(-12px) scale(0.92);
}
.float-pill-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

/* Join Button */
.join-button {
  animation: slideInFromRight 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 4px 12px rgba(88, 168, 225, 0.3) !important;
}

.join-button:hover {
  box-shadow: 0 6px 20px rgba(88, 168, 225, 0.4) !important;
  transform: translateY(-2px) scale(1.05);
}

.join-button:active {
  transform: translateY(-1px) scale(1.02);
  transition: all 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Ensure Join button text color is always correct */
.navbar-glass .join-button {
  color: white !important;
  background: #58a8e1 !important;
  border-color: #58a8e1 !important;
}

.navbar-glass .join-button:hover {
  color: #58a8e1 !important;
  background: white !important;
}

@keyframes slideInFromRight {
  0% {
    opacity: 0;
    transform: translateX(30px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

/* Modern Mobile Menu Dropdown */
.mobile-menu-dropdown {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.5);
  padding: 12px 8px;
  min-width: 180px;
  width: auto;
}

.mobile-menu-item {
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #1a1a1a;
  text-align: left;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.mobile-menu-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background: linear-gradient(135deg, #58a8e1 0%, #4ba8c9 100%);
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-item:hover {
  background: linear-gradient(
    135deg,
    rgba(88, 168, 225, 0.08) 0%,
    rgba(75, 168, 201, 0.05) 100%
  );
  color: #58a8e1;
  transform: translateX(4px);
}

.mobile-menu-item:hover::before {
  transform: scaleY(1);
  transform-origin: top;
}

.mobile-menu-item:active {
  transform: translateX(2px) scale(0.98);
}

/* Active state for router links */
.mobile-menu-item.router-link-active {
  background: linear-gradient(
    135deg,
    rgba(88, 168, 225, 0.12) 0%,
    rgba(75, 168, 201, 0.08) 100%
  );
  color: #58a8e1;
  font-weight: 600;
}

.mobile-menu-item.router-link-active::before {
  transform: scaleY(1);
}

/* Ensure Log in button text is white on mobile and desktop */
.mobile-login-button,
.mobile-login-button span,
.desktop-login-button,
.desktop-login-button span {
  color: white !important;
}

.mobile-login-button:hover,
.desktop-login-button:hover {
  color: #58a8e1 !important;
}

.mobile-login-button:hover span,
.desktop-login-button:hover span {
  color: #58a8e1 !important;
}

/* TRAVEL text below logo */
.logo-travel-text {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.2em;
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase;
  opacity: 0.75;
  transition: opacity 0.3s ease;
  margin-top: 2px;
  line-height: 1;
}

@media (min-width: 768px) {
  .logo-travel-text {
    font-size: 10px;
    letter-spacing: 0.25em;
    opacity: 0.8;
    color: rgba(0, 0, 0, 0.6);
  }
}

/* Adjust TRAVEL text color based on navbar state */
.navbar-transparent .logo-travel-text {
  color: rgba(255, 255, 255, 0.7);
  opacity: 0.85;
}

.navbar-glass .logo-travel-text {
  color: rgba(0, 0, 0, 0.4);
}

.navbar-wrapper.travel-page .navbar-transparent .logo-travel-text {
  color: rgba(0, 0, 0, 0.4);
}

a:hover .logo-travel-text,
.router-link:hover .logo-travel-text {
  opacity: 0.8;
}
</style>
